import type { TourConfig, TourStep } from './types';
import { isAIConfigured } from '../ai';

/**
 * =============================================================================
 * TOUR STEPS DEFINITIONS
 * =============================================================================
 * Define steps for each tour here. Each step should have:
 * - target: CSS selector for the element to highlight
 * - content: JSX or string content for the tooltip
 * - Optional: placement, title, disableBeacon, etc.
 *
 * TIP: Use data-tour="step-id" attributes on elements for stable selectors
 * =============================================================================
 */

/**
 * Onboarding tour steps - shown to first-time users
 */
const onboardingSteps: TourStep[] = [
  {
    id: 'welcome',
    target: '[data-tour="sidebar"]',
    title: '환영합니다! 👋',
    content: 'Markly에 오신 것을 환영합니다! 북마크를 쉽게 관리하고 정리할 수 있어요. 간단한 사용법을 안내해 드릴게요.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    id: 'collections',
    target: '[data-tour="collections-list"]',
    title: '컬렉션',
    content: '북마크를 주제별로 컬렉션에 정리할 수 있어요. 드래그 앤 드롭으로 쉽게 이동할 수 있습니다.',
    placement: 'right',
  },
  {
    id: 'add-collection',
    target: '[data-tour="add-collection"]',
    title: '컬렉션 만들기',
    content: '새 컬렉션을 만들어 북마크를 분류해보세요. 아이콘과 색상도 선택할 수 있어요.',
    placement: 'right',
  },
  {
    id: 'search',
    target: '[data-tour="search"]',
    title: '검색',
    content: '제목, URL, 태그로 북마크를 빠르게 검색할 수 있어요.',
    placement: 'bottom',
  },
  {
    id: 'add-bookmark',
    target: '[data-tour="add-bookmark"]',
    title: '북마크 추가',
    content: 'URL을 입력하면 자동으로 제목과 설명을 가져와요. AI 기능으로 태그와 요약도 자동 생성됩니다!',
    placement: 'bottom',
  },
  {
    id: 'bulk-select',
    target: '[data-tour="bulk-select"]',
    title: '일괄 선택',
    content: '여러 북마크를 한 번에 선택해서 이동, 보관, 삭제할 수 있어요.',
    placement: 'bottom',
  },
  {
    id: 'ai-settings',
    target: '[data-tour="ai-settings"]',
    title: 'AI 설정',
    content: 'AI 기능을 사용하려면 여기서 API 키를 설정하세요. Groq는 무료로 사용할 수 있어요!',
    placement: 'right',
  },
  {
    id: 'complete',
    target: '[data-tour="sidebar"]',
    title: '준비 완료! 🎉',
    content: '이제 Markly를 사용할 준비가 되었어요. 궁금한 점이 있으면 언제든 도움말에서 가이드를 다시 볼 수 있습니다.',
    placement: 'right',
  },
];

/**
 * AI features tour steps
 */
const aiFeaturesSteps: TourStep[] = [
  {
    id: 'ai-intro',
    target: '[data-tour="ai-settings"]',
    title: 'AI 기능 소개 🤖',
    content: 'Markly는 AI를 활용해 북마크를 더 스마트하게 관리할 수 있어요.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    id: 'ai-providers',
    target: '[data-tour="ai-settings"]',
    title: 'AI 제공자',
    content: 'OpenAI, Claude, Groq 중에서 선택할 수 있어요. Groq는 무료 API 키를 제공합니다!',
    placement: 'right',
  },
  {
    id: 'ai-tagging',
    target: '[data-tour="add-bookmark"]',
    title: 'AI 자동 태깅',
    content: '북마크를 추가할 때 "AI 태그" 버튼을 누르면 AI가 관련 태그를 자동으로 추천해줘요.',
    placement: 'bottom',
  },
  {
    id: 'ai-summary',
    target: '[data-tour="add-bookmark"]',
    title: 'AI 요약',
    content: '"요약 생성" 버튼을 누르면 AI가 페이지 내용을 간단히 요약해줘요.',
    placement: 'bottom',
  },
];

/**
 * Collections management tour
 */
const collectionsSteps: TourStep[] = [
  {
    id: 'collections-intro',
    target: '[data-tour="collections-list"]',
    title: '컬렉션 관리',
    content: '컬렉션을 사용해 북마크를 체계적으로 정리해보세요.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    id: 'create-collection',
    target: '[data-tour="add-collection"]',
    title: '새 컬렉션',
    content: '+ 버튼을 눌러 새 컬렉션을 만들 수 있어요.',
    placement: 'right',
  },
  {
    id: 'drag-bookmark',
    target: '[data-tour="bookmark-list"]',
    title: '드래그 앤 드롭',
    content: '북마크를 드래그해서 원하는 컬렉션에 놓으면 자동으로 이동해요.',
    placement: 'top',
  },
  {
    id: 'collection-context',
    target: '[data-tour="collections-list"]',
    title: '컬렉션 편집',
    content: '컬렉션 위에서 마우스 오른쪽 버튼을 클릭하면 수정하거나 삭제할 수 있어요.',
    placement: 'right',
  },
];

/**
 * Bulk actions tour
 */
const bulkActionsSteps: TourStep[] = [
  {
    id: 'bulk-intro',
    target: '[data-tour="bulk-select"]',
    title: '일괄 작업',
    content: '여러 북마크를 한 번에 관리할 수 있어요.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    id: 'select-mode',
    target: '[data-tour="bulk-select"]',
    title: '선택 모드',
    content: '"선택" 버튼을 눌러 선택 모드로 전환하세요.',
    placement: 'bottom',
  },
  {
    id: 'select-bookmarks',
    target: '[data-tour="bookmark-list"]',
    title: '북마크 선택',
    content: '선택 모드에서 북마크를 클릭하면 선택/해제할 수 있어요. "전체 선택"도 가능합니다.',
    placement: 'top',
  },
  {
    id: 'bulk-actions',
    target: '[data-tour="bulk-select"]',
    title: '일괄 작업 실행',
    content: '선택한 북마크들을 한 번에 이동, 보관, 또는 삭제할 수 있어요.',
    placement: 'bottom',
  },
];

/**
 * =============================================================================
 * TOUR CONFIGURATIONS
 * =============================================================================
 * Register all tours here. Each tour needs:
 * - id: Unique identifier (must be in TourId type)
 * - name: Display name
 * - steps: Array of tour steps
 * - Optional: autoStart, onComplete, etc.
 * =============================================================================
 */
export const tourConfigs: TourConfig[] = [
  {
    id: 'onboarding',
    name: '시작 가이드',
    description: 'Markly의 기본 기능을 알아보세요',
    steps: onboardingSteps,
    autoStart: true,
    autoStartCondition: () => {
      // Auto-start if user hasn't seen it
      return true;
    },
  },
  {
    id: 'ai-features',
    name: 'AI 기능 안내',
    description: 'AI 자동 태깅과 요약 기능을 알아보세요',
    steps: aiFeaturesSteps,
    autoStart: false,
  },
  {
    id: 'collections',
    name: '컬렉션 관리',
    description: '컬렉션으로 북마크를 정리하는 방법',
    steps: collectionsSteps,
    autoStart: false,
  },
  {
    id: 'bulk-actions',
    name: '일괄 작업',
    description: '여러 북마크를 한 번에 관리하기',
    steps: bulkActionsSteps,
    autoStart: false,
  },
  {
    id: 'keyboard-shortcuts',
    name: '키보드 단축키',
    description: '단축키로 더 빠르게 사용하기',
    steps: [], // TODO: Add keyboard shortcuts guide
    autoStart: false,
  },
];

/**
 * Get tour config by ID
 */
export function getTourConfig(tourId: string): TourConfig | undefined {
  return tourConfigs.find((t) => t.id === tourId);
}

/**
 * Get all available tours (with valid targets)
 */
export function getAvailableTours(): TourConfig[] {
  return tourConfigs.filter((tour) => tour.steps.length > 0);
}

/**
 * =============================================================================
 * HOW TO ADD A NEW TOUR
 * =============================================================================
 *
 * 1. Add tour ID to TourId type in types.ts:
 *    export type TourId = 'onboarding' | 'my-new-tour' | ...
 *
 * 2. Define steps array:
 *    const myNewTourSteps: TourStep[] = [
 *      {
 *        id: 'step-1',
 *        target: '[data-tour="my-element"]',
 *        title: '제목',
 *        content: '설명',
 *      },
 *      // ... more steps
 *    ];
 *
 * 3. Add tour config to tourConfigs array:
 *    {
 *      id: 'my-new-tour',
 *      name: '새 가이드',
 *      description: '설명',
 *      steps: myNewTourSteps,
 *    }
 *
 * 4. Add data-tour attributes to target elements:
 *    <button data-tour="my-element">Click me</button>
 *
 * =============================================================================
 */
